import {Dispatch, PropsWithChildren, useReducer} from "react";
import {createContext} from "../create-context";
import {Alert, Spin} from "antd";
import {useDigitalProducts} from "../../features/digital-products";
import {
  TGetAllProductsState,
  initialGetAllProductsState,
  createProductFormReducer,
  TGetAllProductsActions,
} from "../../reducers/products/get-all-products-reducer";
import {TGetProductsApiResponse} from "../../react-query-hooks/products";

type TProductsContextState = {
  state: TGetAllProductsState;
  dispatch: Dispatch<TGetAllProductsActions>;
};

const [useContext, ContextProvider] = createContext<TProductsContextState>();

export const useProductsContext = useContext;

export const ProductsProvider = ({children}: PropsWithChildren) => {
  const [reducerState, dispatch] = useReducer(
    createProductFormReducer,
    initialGetAllProductsState
  );
  const getProductsQuery = useDigitalProducts(
    reducerState.currentPage,
    reducerState.per_page
  );

  if (getProductsQuery.isLoading) {
    return (
      <div style={{marginTop: "2.4rem", minHeight: 120, textAlign: "center"}}>
        <Spin size="large" />
      </div>
    );
  }

  if (getProductsQuery.isError) {
    return (
      <div style={{marginTop: "2.4rem"}}>
        <Alert
          message="Error"
          description={getProductsQuery.error.message}
          type="error"
          showIcon
        />
      </div>
    );
  }

  const domainProducts = getProductsQuery.data?.data ?? [];

  const products: TGetProductsApiResponse["products"] = domainProducts.map((product) => ({
    id: product.id,
    userId: 0,
    title: product.name,
    slug: "",
    tiny_url: product.tinyUrl,
    coverImage: product.imageUrl ?? "",
    cover_images: [],
    hideFromProfile: false,
    noOfSales: product.totalPurchases,
    revenue: product.totalRevenue,
    defaultPrice: product.currency
      ? {amount: product.amount, currency: product.currency}
      : null,
    description: "",
    is_deleted: false,
    productCurrencies: [] as [],
    reference: "",
    request_phone_number: false,
    statusId: "1",
    createdAt: "",
    updatedAt: "",
  }));

  const state: TGetAllProductsState = {
    ...reducerState,
    products,
    productMeta: {
      current_page: reducerState.currentPage,
      next_page: getProductsQuery.data?.hasNextPage ? reducerState.currentPage + 1 : 0,
      per_page: reducerState.per_page,
      previous_page: getProductsQuery.data?.hasPrevPage
        ? reducerState.currentPage - 1
        : 0,
      total_pages: Math.ceil((getProductsQuery.data?.total ?? 0) / reducerState.per_page),
    },
    isPreviousData: getProductsQuery.isPreviousData ?? false,
    isLoading: getProductsQuery.isFetching,
  };

  return <ContextProvider value={{state, dispatch}}>{children}</ContextProvider>;
};
